import React from 'react'
import {
  Alert, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material'
import {
  BatchRegenerateState, blueBoxSx, myTablesPath,
  describeTop, describeFilter, describeWeight, describeCaseFilter, describeCells, describeDecimals,
} from './regenerateShared'

/** Step "Review and generate": the confirm, the settings, every table. */
const StepReview: React.FC<{ batch: BatchRegenerateState }> = ({ batch }) => {
  const previews = batch.buildTablePreviews()
  const count = previews.length
  const plural = count !== 1 ? 's' : ''
  const destination = myTablesPath(batch.destinationFolder)
  const hasFolders = previews.some(p => p.folder)
  const settings: [string, string][] = [
    ['Saving to', destination],
    ['Top', describeTop(batch.topMode, batch.topValue)],
    ['Filter', describeFilter(batch.filterMode, batch.filterValue)],
    ['Weight', describeWeight(batch.weightMode, batch.weightValue)],
    ['Case filter', describeCaseFilter(batch.caseFilterMode, batch.caseFilterValue)],
    ['Cell Display', describeCells(batch.cellsMode, batch.cells)],
    ['Decimal places', describeDecimals(batch.decimalsMode, batch.decimals, batch.decimalsInvalid)],
  ]

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        You&apos;re about to {batch.overwrite ? 'overwrite' : 'generate'} {count.toLocaleString()} table{plural}. Continue?
      </Typography>
      {batch.overwrite && (
        <Alert severity="error" data-testid="overwrite-warning" sx={{ mb: 2 }}>
          This replaces the {count.toLocaleString()} table{plural} in {destination}. It cannot be undone.
        </Alert>
      )}
      {batch.caseFilterEmpty && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No cases pass the case filter {batch.caseFilterValue} in {batch.sourceTables[0]?.name}, so the tables would show 0.
        </Alert>
      )}
      <Box sx={{ ...blueBoxSx, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '150px 1fr' }, columnGap: 2, rowGap: 1 }}>
        {settings.map(([label, value]) => (
          <React.Fragment key={label}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{value}</Typography>
          </React.Fragment>
        ))}
      </Box>

      <Typography variant="subtitle2" sx={{ mt: 3, mb: 1, color: 'text.secondary' }}>
        Tables ({count.toLocaleString()})
      </Typography>
      <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1, maxHeight: 320 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Table name</TableCell>
              {hasFolders && <TableCell sx={{ fontWeight: 600 }}>Folder</TableCell>}
              <TableCell sx={{ fontWeight: 600 }}>From</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {previews.map((p, i) => (
              <TableRow key={`${p.folder || ''}/${p.variable}`}>
                <TableCell>{i + 1}</TableCell>
                <TableCell>{p.name}</TableCell>
                {hasFolders && <TableCell>{p.folder || '—'}</TableCell>}
                <TableCell sx={{ color: 'text.secondary' }}>{p.variable}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

export default StepReview
